import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi, cemeteryApi, graveSitesApi, visitsApi } from '../services/api';
import type { VisitTrendGranularity, VisitTrendPoint, VisitDistributionPoint, VisitTopDeceasedPoint } from '../types';
import { confirm } from 'devextreme/ui/dialog';
import { DataGrid, Column, Editing, Paging, FilterRow, HeaderFilter, SearchPanel, Toolbar, Item, Form, Popup as DataGridPopup, MasterDetail, Pager, FilterPanel, Selection } from 'devextreme-react/data-grid';
import { Item as FormItem } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { Tabs } from 'devextreme-react/tabs';
import { FileUploader } from 'devextreme-react/file-uploader';
import Chart, { ArgumentAxis, CommonSeriesSettings, Legend, Series, Tooltip } from 'devextreme-react/chart';
import { Popup } from 'devextreme-react/popup';
import { DateBox } from 'devextreme-react/date-box';
import { SelectBox } from 'devextreme-react/select-box';
import { TagBox } from 'devextreme-react/tag-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import { TextBox } from 'devextreme-react/text-box';
import { CheckBox } from 'devextreme-react/check-box';
import notify from 'devextreme/ui/notify';
import GraveSitePopupEditor from '../components/GraveSitePopupEditor';
import './Administration.css';
import type { CemeteryReferencePoint, CemeterySummary, CreateCemeteryData, OverlayPlacement, UpdateCemeteryData, UpdateCemeteryRequest } from '../types';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import DistortableImageLayer from '../components/DistortableImageLayer';
import { createPlacementFromBounds, getOverlayPlacement, getPlacementFromOverlayInstance, getReferencePointIconOption, normalizeOverlayOpacity, overlayPlacementToPayload, projectReferencePoint, REFERENCE_POINT_ICON_OPTIONS, unprojectReferencePoint } from '../lib/cemeteryOverlay';
import type { ReferencePointIconOption } from '../lib/cemeteryOverlay';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DEFAULT_CEMETERY_LOCATION = {
  southwestLat: 46.133992,
  southwestLng: 14.997715,
  northeastLat: 46.135867,
  northeastLng: 14.999685,
  centerLat: 46.1349295,
  centerLng: 14.9987,
  defaultZoom: 19,
};

const DEFAULT_CEMETERY_BOUNDS: [[number, number], [number, number]] = [
  [DEFAULT_CEMETERY_LOCATION.southwestLat, DEFAULT_CEMETERY_LOCATION.southwestLng],
  [DEFAULT_CEMETERY_LOCATION.northeastLat, DEFAULT_CEMETERY_LOCATION.northeastLng],
];

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const getApiValidationMessage = (error: unknown): string | null => {
  const responseData = (error as { response?: { data?: any } })?.response?.data;
  if (!responseData) {
    return null;
  }

  if (typeof responseData.error === 'string' && responseData.error.trim()) {
    return responseData.error;
  }

  const validationErrors = responseData.errors;
  if (!validationErrors || typeof validationErrors !== 'object') {
    return null;
  }

  const messages = Object.values(validationErrors)
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  return messages.length > 0 ? messages[0] : null;
};

const createReferencePointMapIcon = (iconKey: string, name: string): L.DivIcon => {
  const iconOption = getReferencePointIconOption(iconKey);

  return L.divIcon({
    className: 'overlay-reference-point-wrapper',
    iconSize: [44, 44],
    iconAnchor: [22, 40],
    popupAnchor: [0, -34],
    html: `<div style="display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:10px;background:${iconOption.color};border:2px solid #ffffff;font-size:18px;line-height:1;box-shadow:0 2px 8px rgba(0,0,0,0.35);" title="${escapeHtml(name)}">${escapeHtml(iconOption.glyph)}</div>`,
  });
};

const renderReferencePointIconOption = (option?: ReferencePointIconOption | null) => {
  const icon = option ?? getReferencePointIconOption('other');

  return (
    <div className="referencePointIconOption">
      <span className="referencePointIconPreview" style={{ backgroundColor: icon.color }}>
        {icon.glyph}
      </span>
      <span>{icon.label}</span>
    </div>
  );
};

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

type LocationSelection = {
  centerLat: number;
  centerLng: number;
  defaultZoom: number;
};

type ReferencePointDraft = {
  id: string;
  name: string;
  iconKey: string;
  description: string;
  latitude: number;
  longitude: number;
  x?: number;
  y?: number;
  showOnPublicMap: boolean;
};

type OverlayEditorMode = 'drag' | 'scale' | 'rotate' | 'freeRotate';

const CemeteryLocationMapSync: React.FC<{
  position: [number, number];
  zoom: number;
  onChange: (selection: LocationSelection) => void;
}> = ({ position, zoom, onChange }) => {
  const map = useMap();

  useEffect(() => {
    const center = map.getCenter();
    const currentZoom = map.getZoom();
    const isSameCenter = Math.abs(center.lat - position[0]) < 1e-8 && Math.abs(center.lng - position[1]) < 1e-8;

    if (!isSameCenter || currentZoom !== zoom) {
      map.setView(position, zoom, { animate: false });
    }
  }, [map, position, zoom]);

  useEffect(() => {
    const updateSelection = () => {
      const center = map.getCenter();

      onChange({
        centerLat: center.lat,
        centerLng: center.lng,
        defaultZoom: map.getZoom(),
      });
    };

    updateSelection();
    map.on('moveend', updateSelection);
    map.on('zoomend', updateSelection);

    return () => {
      map.off('moveend', updateSelection);
      map.off('zoomend', updateSelection);
    };
  }, [map, onChange]);

  return null;
};

const CemeteryCenterPin: React.FC<{
  position: [number, number];
  onPositionChange: (position: [number, number]) => void;
}> = ({ position, onPositionChange }) => {
  const map = useMap();

  return (
    <Marker
      position={position}
      draggable={true}
      eventHandlers={{
        dragend: (event) => {
          const marker = event.target as L.Marker;
          const latLng = marker.getLatLng();
          onPositionChange([latLng.lat, latLng.lng]);
          map.panTo(latLng, { animate: false });
        },
      }}
    />
  );
};

const CalibrationMapReferencePointCreator: React.FC<{
  enabled: boolean;
  onMapClick: (lat: number, lng: number) => void;
}> = ({ enabled, onMapClick }) => {
  useMapEvents({
    click: (event) => {
      if (!enabled) {
        return;
      }

      onMapClick(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
};

const CalibrationReferencePointMarker: React.FC<{
  point: ReferencePointDraft & { position: [number, number]; markerIcon: L.DivIcon };
  dragEnabled: boolean;
  onEdit: (pointId: string) => void;
  onMove: (pointId: string, lat: number, lng: number) => void;
}> = ({ point, dragEnabled, onEdit, onMove }) => {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!dragEnabled) {
      markerRef.current?.dragging?.disable();
    }
  }, [dragEnabled]);

  return (
    <Marker
      ref={markerRef}
      position={point.position}
      icon={point.markerIcon}
      title={point.name}
      draggable={false}
      eventHandlers={{
        mouseover: () => {
          if (!dragEnabled) {
            return;
          }

          markerRef.current?.dragging?.enable();
        },
        mouseout: () => {
          if (isDraggingRef.current) {
            return;
          }

          markerRef.current?.dragging?.disable();
        },
        dragstart: () => {
          if (!dragEnabled) {
            return;
          }

          isDraggingRef.current = true;
          map.dragging.disable();
        },
        dragend: (event) => {
          const marker = event.target as L.Marker;
          const latLng = marker.getLatLng();

          isDraggingRef.current = false;
          marker.dragging?.disable();
          map.dragging.enable();
          onMove(point.id, latLng.lat, latLng.lng);
        },
        click: () => onEdit(point.id),
      }}
    />
  );
};

type ActiveTab = 'deceased' | 'users' | 'cemetery' | 'analytics';

const Administration: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('deceased');
  const usersDataGridRef = useRef<any>(null);
  const graveSitesDataGridRef = useRef<any>(null);
  
  // Cemetery photo state
  const [currentCemeteryPhoto, setCurrentCemeteryPhoto] = useState<string | null>(null);
  const [cemeteryDataId, setCemeteryDataId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [overlayPlacement, setOverlayPlacement] = useState<OverlayPlacement | null>(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0.4);
  const [overlayEditorMode] = useState<OverlayEditorMode>('freeRotate');
  const [preserveAspectRatio, setPreserveAspectRatio] = useState(true);
  const [isAddingReferencePoint, setIsAddingReferencePoint] = useState(false);
  const [referencePoints, setReferencePoints] = useState<ReferencePointDraft[]>([]);
  const [referencePointDraft, setReferencePointDraft] = useState<ReferencePointDraft | null>(null);
  const [isReferencePointPopupVisible, setIsReferencePointPopupVisible] = useState(false);
  const [photoFileName, setPhotoFileName] = useState<string>('');
  const [photoDimensions, setPhotoDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const overlayRef = useRef<L.DistortableImageOverlay | null>(null);
  
  // Categories state for autocomplete
  const [categories, setCategories] = useState<string[]>([]);
  
  // GraveSite popup editor state
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupData, setPopupData] = useState<any>(null);
  const [isNewGraveSite, setIsNewGraveSite] = useState(false);
  
  // Pager configuration
  const allowedPageSizes = [5, 10, 20, 50];
  const displayMode = 'adaptive';
  const showPageSizeSelector = true;
  const showInfo = true;
  const showNavButtons = true;

  const now = new Date();
  const [analyticsStartDate, setAnalyticsStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
  const [analyticsEndDate, setAnalyticsEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [trendGranularity, setTrendGranularity] = useState<VisitTrendGranularity>('day');
  const [trendData, setTrendData] = useState<VisitTrendPoint[]>([]);
  const [weekdayData, setWeekdayData] = useState<VisitDistributionPoint[]>([]);
  const [hourlyData, setHourlyData] = useState<VisitDistributionPoint[]>([]);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [topDeceasedData, setTopDeceasedData] = useState<VisitTopDeceasedPoint[]>([]);
  const [cemeteries, setCemeteries] = useState<CemeterySummary[]>([]);
  const [selectedCemeterySlug, setSelectedCemeterySlug] = useState<string>('');
  const selectedCemetery = cemeteries.find((c) => c.slug === selectedCemeterySlug) ?? null;
  const [cemeteryName, setCemeteryName] = useState('');
  const [cemeterySlug, setCemeterySlug] = useState('');
  const [cemeteryCenterLat, setCemeteryCenterLat] = useState<number | null>(null);
  const [cemeteryCenterLng, setCemeteryCenterLng] = useState<number | null>(null);
  const [cemeteryDefaultZoom, setCemeteryDefaultZoom] = useState<number | null>(null);
  const [isSavingCemeterySettings, setIsSavingCemeterySettings] = useState(false);
  const [analyticsSelectedCemeterySlugs, setAnalyticsSelectedCemeterySlugs] = useState<string[]>([]);
  const [isCreateCemeteryPopupVisible, setIsCreateCemeteryPopupVisible] = useState(false);
  const [newCemeteryName, setNewCemeteryName] = useState('');
  const [newCemeterySlug, setNewCemeterySlug] = useState('');
  const [isCreatingCemetery, setIsCreatingCemetery] = useState(false);
  const [calibrationMapInstanceKey, setCalibrationMapInstanceKey] = useState(0);
  const cemeteryDataRequestIdRef = useRef(0);
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const slugify = (value: string): string => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const cemeteryLocationValue = {
    centerLat: cemeteryCenterLat ?? DEFAULT_CEMETERY_LOCATION.centerLat,
    centerLng: cemeteryCenterLng ?? DEFAULT_CEMETERY_LOCATION.centerLng,
    defaultZoom: cemeteryDefaultZoom ?? DEFAULT_CEMETERY_LOCATION.defaultZoom,
  };
  const overlayPreviewPoints = useMemo(() => referencePoints
    .filter((point) => point.showOnPublicMap)
    .map((point) => {
      let position: [number, number] | null = null;

      if (Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) {
        position = [point.latitude, point.longitude];
      } else if (
        overlayPlacement
        && Number.isFinite(point.x)
        && Number.isFinite(point.y)
      ) {
        position = projectReferencePoint({ x: point.x as number, y: point.y as number }, overlayPlacement);
      }

      return {
        ...point,
        position,
        icon: getReferencePointIconOption(point.iconKey),
        markerIcon: createReferencePointMapIcon(point.iconKey, point.name),
      };
    })
    .filter((point): point is (ReferencePointDraft & { position: [number, number]; icon: ReturnType<typeof getReferencePointIconOption>; markerIcon: L.DivIcon }) => point.position != null), [overlayPlacement, referencePoints]);

  const syncSelectedCemeteryForm = (cemetery: CemeterySummary | null) => {
    if (!cemetery) {
      return;
    }

    setCemeteryName(cemetery.name);
    setCemeterySlug(cemetery.slug);
    setCemeteryCenterLat(cemetery.centerLat ?? DEFAULT_CEMETERY_LOCATION.centerLat);
    setCemeteryCenterLng(cemetery.centerLng ?? DEFAULT_CEMETERY_LOCATION.centerLng);
    setCemeteryDefaultZoom(cemetery.defaultZoom ?? DEFAULT_CEMETERY_LOCATION.defaultZoom);
  };

  const loadCemeteries = async (preferredSlug?: string) => {
    try {
      const list = await cemeteryApi.list(true);
      setCemeteries(list);

      setAnalyticsSelectedCemeterySlugs((prev) => {
        if (prev.length === 0) {
          return list.map((c) => c.slug);
        }

        const valid = prev.filter((slug) => list.some((c) => c.slug === slug));
        return valid.length > 0 ? valid : list.map((c) => c.slug);
      });

      const hasCurrentSelection = selectedCemeterySlug && list.some(c => c.slug === selectedCemeterySlug);
      if (hasCurrentSelection) {
        return;
      }

      const savedSelection = preferredSlug
        ? list.find(c => c.slug === preferredSlug)
        : undefined;
      const fallback = list.find(c => c.isActive) ?? list[0];
      const nextSelection = savedSelection?.slug ?? fallback?.slug;

      if (nextSelection) {
        setSelectedCemeterySlug(nextSelection);
        localStorage.setItem('adminSelectedCemeterySlug', nextSelection);
      }
    } catch (error) {
      console.error('Failed to load cemeteries:', error);
      notify('Napaka pri nalaganju pokopališč.', 'error', 3000);
    }
  };

  const handleCreateCemetery = async () => {
    if (!newCemeteryName.trim()) {
      notify('Vnesite ime pokopališča.', 'warning', 2500);
      return;
    }

    const computedSlug = newCemeterySlug.trim() || slugify(newCemeteryName);
    if (!computedSlug) {
      notify('Slug ni veljaven.', 'warning', 2500);
      return;
    }

    try {
      setIsCreatingCemetery(true);
      const created = await cemeteryApi.createCemetery({
        name: newCemeteryName.trim(),
        slug: computedSlug,
        isActive: true,
      });

      const updated = [...cemeteries, created].sort((a, b) => (a.displayOrder - b.displayOrder) || (a.id - b.id));
      setCemeteries(updated);
      setSelectedCemeterySlug(created.slug);
      localStorage.setItem('adminSelectedCemeterySlug', created.slug);
      setIsCreateCemeteryPopupVisible(false);
      setNewCemeteryName('');
      setNewCemeterySlug('');
      notify('Pokopališče je dodano.', 'success', 2500);
    } catch (error) {
      console.error('Failed to create cemetery:', error);
      notify('Napaka pri dodajanju pokopališča.', 'error', 3000);
    } finally {
      setIsCreatingCemetery(false);
    }
  };

  const handleSaveCemeterySettings = async () => {
    if (!selectedCemetery) {
      notify('Najprej izberite pokopališče.', 'warning', 2500);
      return;
    }

    const trimmedName = cemeteryName.trim();
    if (!trimmedName) {
      notify('Ime pokopališča je obvezno.', 'warning', 2500);
      return;
    }

    const computedSlug = slugify(cemeterySlug || trimmedName);
    if (!computedSlug) {
      notify('Slug ni veljaven.', 'warning', 2500);
      return;
    }

    try {
      setIsSavingCemeterySettings(true);

      const payload: UpdateCemeteryRequest = {
        name: trimmedName,
        slug: computedSlug,
        isActive: selectedCemetery.isActive,
        displayOrder: selectedCemetery.displayOrder,
        centerLat: cemeteryCenterLat ?? DEFAULT_CEMETERY_LOCATION.centerLat,
        centerLng: cemeteryCenterLng ?? DEFAULT_CEMETERY_LOCATION.centerLng,
        defaultZoom: cemeteryDefaultZoom ?? DEFAULT_CEMETERY_LOCATION.defaultZoom,
      };

      const updated = await cemeteryApi.updateCemetery(selectedCemetery.id, payload);
      setCemeteries((prev) => prev.map((cemetery) => (cemetery.id === updated.id ? updated : cemetery)));
      setSelectedCemeterySlug(updated.slug);
      localStorage.setItem('adminSelectedCemeterySlug', updated.slug);
      syncSelectedCemeteryForm(updated);
      notify('Podatki pokopališča so shranjeni.', 'success', 2500);
    } catch (error) {
      console.error('Failed to save cemetery settings:', error);
      notify('Napaka pri shranjevanju podatkov pokopališča.', 'error', 3000);
    } finally {
      setIsSavingCemeterySettings(false);
    }
  };

  // Load existing cemetery data on component mount
  useEffect(() => {
    if (activeTab === 'cemetery') {
      void loadCemeteryData(selectedCemeterySlug);
    }
  }, [activeTab, selectedCemeterySlug]);

  useEffect(() => {
    const saved = localStorage.getItem('adminSelectedCemeterySlug');
    if (saved) {
      setSelectedCemeterySlug(saved);
    }

    void loadCemeteries(saved ?? undefined);
  }, []);

  useEffect(() => {
    syncSelectedCemeteryForm(selectedCemetery);
  }, [selectedCemetery]);

  useEffect(() => {
    if (activeTab !== 'analytics') {
      return;
    }

    void loadVisitStatistics();
  }, [activeTab, analyticsStartDate, analyticsEndDate, trendGranularity, analyticsSelectedCemeterySlugs]);

  // Load categories for autocomplete
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await graveSitesApi.getCategories(selectedCemeterySlug || undefined);
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };

    if (selectedCemeterySlug) {
      loadCategories();
    }
  }, [selectedCemeterySlug]);

  const resetLoadedCemeteryData = () => {
    setCemeteryDataId(null);
    setCurrentCemeteryPhoto(null);
    setPhotoFileName('');
    setPhotoDimensions({ width: 0, height: 0 });
    setOverlayPlacement(null);
    setOverlayOpacity(0.4);
    setReferencePoints([]);
    setIsAddingReferencePoint(false);
    setCalibrationMapInstanceKey((prev) => prev + 1);
  };

  const loadCemeteryData = async (targetCemeterySlug?: string) => {
    const slug = targetCemeterySlug || selectedCemeterySlug;
    const requestId = ++cemeteryDataRequestIdRef.current;

    if (!slug) {
      resetLoadedCemeteryData();
      return;
    }

    try {
      const data = await cemeteryApi.getCemeteryData(slug);

      if (requestId !== cemeteryDataRequestIdRef.current) {
        return;
      }

      if (data) {
        setCemeteryDataId(data.id);
        setCemeteryCenterLat(data.centerLat ?? DEFAULT_CEMETERY_LOCATION.centerLat);
        setCemeteryCenterLng(data.centerLng ?? DEFAULT_CEMETERY_LOCATION.centerLng);
        setCemeteryDefaultZoom(data.defaultZoom ?? DEFAULT_CEMETERY_LOCATION.defaultZoom);
        
        // Construct the full image URL for production and development
        const imageUrl = data.photoPath.startsWith('http') 
          ? data.photoPath  // Absolute URL from API
          : `${window.location.origin}${data.photoPath}`; // Relative path, make it absolute
        
        console.log('Loading existing cemetery photo from:', imageUrl);
        setCurrentCemeteryPhoto(imageUrl);
        setPhotoFileName(data.photoFileName);
        setPhotoDimensions({ width: data.photoWidth, height: data.photoHeight });
        const resolvedPlacement = getOverlayPlacement(data) ?? createPlacementFromBounds(DEFAULT_CEMETERY_BOUNDS[0], DEFAULT_CEMETERY_BOUNDS[1], 0.4);
        setOverlayPlacement(resolvedPlacement);
        setOverlayOpacity(normalizeOverlayOpacity(data.overlayOpacity, 0.4));
        setIsAddingReferencePoint(false);

        const refPoints = data.referencePoints.map((rp: CemeteryReferencePoint) => ({
          id: rp.id.toString(),
          name: rp.name,
          iconKey: rp.iconKey,
          description: rp.description ?? '',
          latitude: Number.isFinite(rp.latitude)
            ? (rp.latitude as number)
            : (Number.isFinite(rp.x) && Number.isFinite(rp.y)
              ? projectReferencePoint({ x: rp.x as number, y: rp.y as number }, resolvedPlacement)[0]
              : cemeteryLocationValue.centerLat),
          longitude: Number.isFinite(rp.longitude)
            ? (rp.longitude as number)
            : (Number.isFinite(rp.x) && Number.isFinite(rp.y)
              ? projectReferencePoint({ x: rp.x as number, y: rp.y as number }, resolvedPlacement)[1]
              : cemeteryLocationValue.centerLng),
          x: rp.x,
          y: rp.y,
          showOnPublicMap: rp.showOnPublicMap,
        }));
        setReferencePoints(refPoints);
        setCalibrationMapInstanceKey((prev) => prev + 1);
      } else {
        resetLoadedCemeteryData();
      }
    } catch (error) {
      if (requestId !== cemeteryDataRequestIdRef.current) {
        return;
      }

      resetLoadedCemeteryData();
      console.error('Failed to load cemetery data:', error);
    }
  };

  const handleResetPassword = async (userId: string) => {
    try {
      const confirmed = await confirm(
        'Ali ste prepričani, da želite ponastaviti geslo tega uporabnika? Uporabnik bo moral nastaviti novo geslo ob naslednji prijavi.',
        'Ponastavitev gesla'
      );
      
      if (confirmed) {
        await usersApi.resetPassword(userId);
        // Force refresh of the DataGrid to show updated hasPassword status
        if (usersDataGridRef.current) {
          usersDataGridRef.current.instance().getDataSource().reload();
        }
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      alert('Napaka pri ponastavitvi gesla. Preverite konzolo za podrobnosti.');
    }
  };

  const handleFileUpload = async (e: any) => {
    try {
      setIsUploading(true);
      
      // DevExpress FileUploader provides files in the value array
      const files = e.value;
      if (!files || files.length === 0) {
        setIsUploading(false);
        return;
      }
      
      const file = files[0]; // Get the first (and only) file
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        notify('Prosim izberite veljavno slikovno datoteko.', 'error', 3000);
        setIsUploading(false);
        return;
      }
      
      // Upload to backend API
      const uploadResult = await cemeteryApi.uploadPhoto(file);
      console.log('Upload result:', uploadResult);
      
      // Construct the full image URL for production and development
      const imageUrl = uploadResult.photoPath.startsWith('http') 
        ? uploadResult.photoPath  // Absolute URL from API
        : `${window.location.origin}${uploadResult.photoPath}`; // Relative path, make it absolute
      
      console.log('Loading image from URL:', imageUrl);
      
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        console.log('Image loaded successfully:', img.width, 'x', img.height);
        setPhotoDimensions({ width: img.width, height: img.height });
        setCurrentCemeteryPhoto(imageUrl);
        setPhotoFileName(uploadResult.fileName);
        setOverlayPlacement(
          createPlacementFromBounds(
            DEFAULT_CEMETERY_BOUNDS[0],
            DEFAULT_CEMETERY_BOUNDS[1],
            0.4,
          ),
        );
        setOverlayOpacity(0.4);
        setIsAddingReferencePoint(false);
        
        // Reset calibration when new photo is uploaded
        setReferencePoints([]);
        setCemeteryDataId(null);
        
        notify('Fotografija pokopališča je bila uspešno naložena!', 'success', 3000);
        setIsUploading(false);
      };
      
      img.onerror = (error) => {
        console.error('Image load error:', error);
        console.error('Failed to load image from URL:', imageUrl);
        console.error('Upload result photoPath:', uploadResult.photoPath);
        notify('Napaka pri branju slike. URL: ' + imageUrl, 'error', 5000);
        setIsUploading(false);
      };
      
      img.src = imageUrl;
      
    } catch (error) {
      console.error('File upload failed:', error);
      notify('Napaka pri nalaganju fotografije. Preverite konzolo za podrobnosti.', 'error', 3000);
      setIsUploading(false);
    }
  };

  const handleCreateReferencePointDraft = (draft: ReferencePointDraft) => {
    setReferencePointDraft(draft);
    setIsReferencePointPopupVisible(true);
    setIsAddingReferencePoint(false);
  };

  const handleReferencePointMapClick = (lat: number, lng: number) => {
    const currentPlacement = getPlacementFromOverlayInstance(overlayRef.current, overlayOpacity) ?? overlayPlacement;
    const projectedPoint = currentPlacement
      ? unprojectReferencePoint(lat, lng, currentPlacement)
      : null;

    handleCreateReferencePointDraft({
      id: Date.now().toString(),
      name: '',
      iconKey: 'gate',
      description: '',
      latitude: lat,
      longitude: lng,
      x: projectedPoint?.x,
      y: projectedPoint?.y,
      showOnPublicMap: true,
    });
  };

  const handleSaveReferencePoint = () => {
    if (!referencePointDraft?.name.trim()) {
      notify('Vnesite ime referenčne točke.', 'warning', 2500);
      return;
    }

    setReferencePoints((prev) => {
      const normalizedPoint = {
        ...referencePointDraft,
        name: referencePointDraft.name.trim(),
      };
      const existingIndex = prev.findIndex((point) => point.id === normalizedPoint.id);

      if (existingIndex === -1) {
        return [...prev, normalizedPoint];
      }

      const next = [...prev];
      next[existingIndex] = normalizedPoint;
      return next;
    });
    setIsReferencePointPopupVisible(false);
    setReferencePointDraft(null);
    notify('Referenčna točka je dodana.', 'success', 2000);
  };

  const handleEditReferencePoint = (pointId: string) => {
    const point = referencePoints.find((item) => item.id === pointId);
    if (!point) {
      return;
    }

    setReferencePointDraft({ ...point });
    setIsReferencePointPopupVisible(true);
  };

  const handleMoveReferencePoint = (pointId: string, lat: number, lng: number) => {
    const currentPlacement = getPlacementFromOverlayInstance(overlayRef.current, overlayOpacity) ?? overlayPlacement;
    const projectedPoint = currentPlacement ? unprojectReferencePoint(lat, lng, currentPlacement) : null;

    setReferencePoints((prev) => prev.map((point) => point.id === pointId
      ? {
          ...point,
          latitude: lat,
          longitude: lng,
          x: projectedPoint?.x,
          y: projectedPoint?.y,
        }
      : point));
  };

  const handleDeleteReferencePoint = (pointId: string) => {
    setReferencePoints((prev) => prev.filter((point) => point.id !== pointId));
  };

  const saveCemeteryCalibration = async () => {
    try {
      setIsSaving(true);
      
      if (!photoFileName) {
        notify('Najprej naložite fotografijo pokopališča.', 'error', 3000);
        return;
      }

      const currentPlacement = getPlacementFromOverlayInstance(overlayRef.current, overlayOpacity) ?? overlayPlacement;
      if (!currentPlacement) {
        notify('Najprej poravnajte fotografijo na zemljevidu.', 'warning', 3000);
        return;
      }

      const normalizedOpacity = normalizeOverlayOpacity(overlayOpacity, 0.4);
      const normalizedPlacement = { ...currentPlacement, opacity: normalizedOpacity };

      const invalidReferencePoint = referencePoints.find((point) => !point.name.trim());
      if (invalidReferencePoint) {
        notify('Vsaka referenčna točka mora imeti ime.', 'warning', 3000);
        return;
      }

      setOverlayOpacity(normalizedOpacity);
      setOverlayPlacement(normalizedPlacement);
      
      const calibrationData: UpdateCemeteryData = {
        ...overlayPlacementToPayload(normalizedPlacement),
        referencePoints: referencePoints
          .map((rp) => ({
          name: rp.name.trim(),
          iconKey: (rp.iconKey || 'other').trim() || 'other',
          description: rp.description?.trim() || undefined,
          latitude: rp.latitude,
          longitude: rp.longitude,
          x: Number.isFinite(rp.x) ? rp.x : undefined,
          y: Number.isFinite(rp.y) ? rp.y : undefined,
          showOnPublicMap: !!rp.showOnPublicMap,
          })),
      };
      
      let result;
      if (cemeteryDataId) {
        // Update existing data
        result = await cemeteryApi.updateCemeteryData(cemeteryDataId, calibrationData, selectedCemeterySlug || undefined);
      } else {
        // Create new data
        const newData: CreateCemeteryData = {
          photoFileName,
          photoWidth: photoDimensions.width,
          photoHeight: photoDimensions.height,
          ...calibrationData
        };
        result = await cemeteryApi.saveCemeteryData(newData, selectedCemeterySlug || undefined);
        setCemeteryDataId(result.id);
      }
      
      notify('Kalibracija pokopališča je bila uspešno shranjena!', 'success', 3000);
    } catch (error) {
      console.error('Failed to save cemetery calibration:', error);
      notify(getApiValidationMessage(error) ?? 'Napaka pri shranjevanju kalibracije. Poskusite znova.', 'error', 4500);
    } finally {
      setIsSaving(false);
    }
  };

  const resetCemeteryCalibration = async () => {
    setIsAddingReferencePoint(false);

    if (cemeteryDataId && selectedCemeterySlug) {
      await loadCemeteryData(selectedCemeterySlug);
      notify('Kalibracija je bila ponastavljena na zadnje shranjeno stanje.', 'info', 2500);
      return;
    }

    setReferencePoints([]);
    setOverlayPlacement(createPlacementFromBounds(DEFAULT_CEMETERY_BOUNDS[0], DEFAULT_CEMETERY_BOUNDS[1], 0.4));
    setOverlayOpacity(0.4);
    notify('Kalibracija je bila ponastavljena na začetno lokalno stanje.', 'info', 2500);
  };

  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadVisitStatistics = async () => {
    if (analyticsStartDate > analyticsEndDate) {
      notify('Začetni datum ne sme biti po končnem datumu.', 'warning', 3000);
      return;
    }

    try {
      setIsAnalyticsLoading(true);

      const params = {
        startDate: formatDateForApi(analyticsStartDate),
        endDate: formatDateForApi(analyticsEndDate),
        cemeterySlugs: analyticsSelectedCemeterySlugs.length > 0 ? analyticsSelectedCemeterySlugs.join(',') : undefined,
      };

      const [trendResponse, weekdayResponse, hourlyResponse, topDeceasedResponse] = await Promise.all([
        visitsApi.getTrend({ ...params, granularity: trendGranularity }),
        visitsApi.getWeekdayDistribution(params),
        visitsApi.getHourlyDistribution(params),
        visitsApi.getTopDeceased({ ...params, limit: 10 }),
      ]);

      setTrendData(trendResponse.data ?? []);
      setWeekdayData(weekdayResponse.data ?? []);
      setHourlyData(hourlyResponse.data ?? []);
      setTopDeceasedData(topDeceasedResponse.data ?? []);
    } catch (error) {
      console.error('Failed to load visit statistics:', error);
      notify('Napaka pri nalaganju statistike obiskov.', 'error', 3000);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };



  const navigationTabs = [
    { id: 'deceased', text: 'Upravljanje pokojnih oseb', icon: 'card' },
    { id: 'users', text: 'Upravljanje uporabnikov', icon: 'user' },
    { id: 'cemetery', text: 'Podatki pokopališča', icon: 'pinmap' },
    { id: 'analytics', text: 'Statistika obiskov', icon: 'chart' }
  ];
  const selectedTabIndex = Math.max(0, navigationTabs.findIndex((tab) => tab.id === activeTab));

  const renderDeceasedManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Upravljanje grobnih mest in pokojnih oseb</h2>
        <p className="sectionDescription">
          Pregled grobnih mest z možnostjo upravljanja pokojnih oseb v posameznem grobu
        </p>
      </div>



      <div className="gridContainer">
        <DataGrid
          ref={graveSitesDataGridRef}
          dataSource={graveSitesApi.createStore(selectedCemeterySlug || undefined)}
          showBorders={true}
          showRowLines={true}
          showColumnLines={false}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
          remoteOperations={{ filtering: true, sorting: true, paging: true }}
          height="auto"
          wordWrapEnabled={true}
          allowColumnReordering={true}
          allowColumnResizing={true}
          //columnResizingMode="widget"
          onEditingStart={async (e) => {
            try {
              // Fetch full grave site data including location and photo fields
              const fullData = await graveSitesApi.getById(e.data.id, selectedCemeterySlug || undefined);
              setPopupData(fullData);
              setIsNewGraveSite(false);
              setPopupVisible(true);
            } catch (error) {
              console.error('Failed to fetch grave site data:', error);
              notify('Napaka pri pridobivanju podatkov', 'error', 3000);
            }
            e.cancel = true;
          }}
          onInitNewRow={(_e) => {
            setPopupData({});
            setIsNewGraveSite(true);
            setPopupVisible(true);
          }}
          onRowInserting={(e) => {
            // Prevent default insert - we handle it in the popup
            e.cancel = true;
          }}
          onRowUpdating={(e) => {
            // Prevent default update - we handle it in the popup
            e.cancel = true;
          }}
          onDataErrorOccurred={(e: any) => {
            const serverMessage = e.error?.responseJSON?.error
              ?? e.error?.response?.data?.error;
            notify(
              serverMessage || 'Napaka pri brisanju grobnega mesta.',
              'error',
              4500,
            );
          }}
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} placeholder="Iskanje po vseh stolpcih..." width={"35vw"} />
          
          <FilterPanel visible={true} />
          <Paging enabled={true} defaultPageSize={10} />
          <Pager
            visible={true}
            allowedPageSizes={allowedPageSizes}
            displayMode={displayMode}
            showPageSizeSelector={showPageSizeSelector}
            showInfo={showInfo}
            showNavigationButtons={showNavButtons} />
          
          <Editing 
            mode="popup" 
            allowUpdating={true} 
            allowAdding={true} 
            allowDeleting={true}
            confirmDelete={true}
            useIcons={true}
            texts={{
              confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati ta zapis?',
              deleteRow: 'Izbriši',
              editRow: 'Uredi',
              saveRowChanges: 'Shrani',
              cancelRowChanges: 'Prekliči',
              addRow: 'Dodaj novo'
            }}
            popup={{
              visible: false // Hide built-in popup, use custom popup instead
            }}
          />

          <Selection mode="multiple" showCheckBoxesMode="always" />

          <Toolbar>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
            <Item name="columnChooserButton" />
            <Item
              widget="dxButton"
              locateInMenu="auto"
              showText="inMenu"
              options={{
                icon: 'xlsxfile',
                //text: 'Izvozi vse',
                hint: 'Izvozi vsa grobna mesta v Excel',
                onClick: async () => {
                  try {
                    await graveSitesApi.exportAll(selectedCemeterySlug || undefined);
                  } catch {
                    notify('Napaka pri izvozu', 'error', 3000);
                  }
                },
              }}
            />
            <Item
              widget="dxButton"
              locateInMenu="auto"
              showText="inMenu"
              options={{
                icon: 'export',
                //text: 'Izvozi izbrane',
                hint: 'Izvozi izbrana grobna mesta v Excel',
                onClick: async () => {
                  const grid = graveSitesDataGridRef.current?.instance();
                  if (!grid) return;
                  const selected: any[] = grid.getSelectedRowsData();
                  if (!selected.length) {
                    notify('Ni izbranih vrstic', 'warning', 2500);
                    return;
                  }
                  const ids: number[] = selected.map((r: any) => r.id);
                  try {
                    await graveSitesApi.exportSelected(ids, selectedCemeterySlug || undefined);
                  } catch {
                    notify('Napaka pri izvozu', 'error', 3000);
                  }
                },
              }}
            />
          </Toolbar>

          {/* Master Grid Columns - Grave Sites */}
          <Column 
            dataField="graveCode" 
            caption="Oznaka groba" 
            minWidth={120}
            allowEditing={false}
          />
          <Column 
            dataField="section" 
            caption="Odsek" 
            minWidth={80}
            validationRules={[{ type: 'required', message: 'Odsek je obvezen' }]}
          />
          <Column 
            dataField="graveRow" 
            caption="Vrsta" 
            minWidth={80}
            dataType="number"
            validationRules={[{ type: 'required', message: 'Vrsta je obvezna' }]}
          />
          <Column 
            dataField="sequentialNumber" 
            caption="Zaporedna" 
            minWidth={100}
            validationRules={[{ type: 'required', message: 'Zaporedna številka je obvezna' }]}
          />
          <Column 
            dataField="category" 
            caption="Kategorija" 
            minWidth={100}
          />
          <Column 
            dataField="department" 
            caption="Oddelek" 
            minWidth={80}
          />
          <Column 
            dataField="deceasedCount" 
            caption="Št. pokojnih" 
            minWidth={100}
            allowEditing={false}
          />
          <Column 
            dataField="deceasedNames" 
            caption="Pokojni" 
            minWidth={200}
            allowEditing={false}
            allowFiltering={true}
            allowSearch={true}
          />
          <Column 
            dataField="isActive" 
            caption="Aktiven" 
            dataType="boolean" 
            minWidth={80}
          />
          
          {/* Command column for edit/delete buttons */}
          <Column 
            type="buttons" 
            fixed={true}
            fixedPosition="right"
            width={100}
            buttons={[
              {
                name: 'edit',
                hint: 'Uredi',
                icon: 'edit'
              },
              {
                name: 'delete',
                hint: 'Izbriši',
                icon: 'trash'
              }
            ]}
          />

          {/* Master-Detail Template for Deceased */}
          <MasterDetail
            enabled={true}
            component={(props: any) => (
              <div style={{ padding: '0px' }}>
                <h4>Pokojni v grobu: {props.data.data.graveCode}</h4>
                <DataGrid
                  dataSource={graveSitesApi.getDeceasedByGrave(props.data.data.id, selectedCemeterySlug || undefined)}
                  showBorders={true}
                  columnAutoWidth={true}
                  //height={350}
                >
                  <Paging enabled={true} defaultPageSize={5} />
                  {/* <Editing 
                    mode="row" 
                    allowUpdating={true} 
                    allowAdding={true} 
                    allowDeleting={true}
                    texts={{
                      confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati ta zapis?',
                      deleteRow: 'Izbriši',
                      editRow: 'Uredi',
                      saveRowChanges: 'Shrani',
                      cancelRowChanges: 'Prekliči',
                      addRow: 'Dodaj pokojnega'
                    }}
                  /> */}
                  
                  <Column dataField="fullName" caption="Polno ime" minWidth={180} />
                  <Column dataField="displayBirthDate" caption="Rojstvo" minWidth={100} />
                  <Column dataField="displayDeathDate" caption="Smrt" minWidth={100} />
                  <Column dataField="age" caption="Starost" minWidth={80} allowEditing={false} />
                  <Column dataField="graveType" caption="Tip groba" minWidth={100} />
                  <Column dataField="allowPublication" caption="Objava dovoljena" dataType="boolean" minWidth={120} />
                </DataGrid>
              </div>
            )}
          />
        </DataGrid>
        
        {/* Custom Popup Editor */}
        <GraveSitePopupEditor
          visible={popupVisible}
          data={popupData}
          isNew={isNewGraveSite}
          cemeterySlug={selectedCemeterySlug || undefined}
          categories={categories}
          onHiding={() => setPopupVisible(false)}
          onSaved={() => {
            if (graveSitesDataGridRef.current) {
              graveSitesDataGridRef.current.instance().refresh();
            }
          }}
        />
      </div>
    </>
  );

  const renderCemeteryManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Podatki pokopališča</h2>
        <p className="sectionDescription">
          Upravljajte podatke pokopališča in nastavite referenčne točke za natančno lociranje grobov
        </p>
      </div>

      <div className="cemeteryContainer">
        <div className="cemeterySettingsSection">
          <div className="cemeterySettingsHeader">
            <h3>Osnovni podatki in lokacija</h3>
          </div>

          {selectedCemetery ? (
            <>
              <div className="cemeterySettingsGrid">
                <div className="cemeteryField">
                  <label>Ime pokopališča</label>
                  <TextBox
                    value={cemeteryName}
                    onValueChanged={(e) => setCemeteryName(e.value ?? '')}
                    placeholder="Ime pokopališča"
                  />
                </div>

                <div className="cemeteryField">
                  <label>Slug (URL)</label>
                  <TextBox
                    value={cemeterySlug}
                    onValueChanged={(e) => setCemeterySlug(slugify(e.value ?? ''))}
                    placeholder="pokopalisce-xxx"
                  />
                </div>
              </div>

              <div className="cemeteryMapSection">
                <div className="cemeteryMapHeader">
                  <h4>Lokacija in poravnava na zemljevidu</h4>
                  <p>Uporabite spodnji zemljevid za določitev centra pokopališča, poravnavo fotografije in dodajanje referenčnih točk.</p>
                </div>
                <div className="cemeteryLocationSummary">
                  <div>Centriranje in povečavo urejate na glavnem zemljevidu spodaj.</div>
                  <div>Povečava: {cemeteryLocationValue.defaultZoom}</div>
                </div>
              </div>

              <div className="cemeterySettingsActions">
                <Button
                  text="Shrani podatke pokopališča"
                  type="default"
                  stylingMode="contained"
                  icon="save"
                  onClick={handleSaveCemeterySettings}
                  disabled={isSavingCemeterySettings}
                />
              </div>
            </>
          ) : (
            <p>Izberite pokopališče, da lahko urejate podatke in lokacijo.</p>
          )}
        </div>

        <div className="uploadSection">
          <h3>Nalaganje letalske fotografije</h3>
          <p className="uploadDescription">
            Naložite visokokakovostno letalsko fotografijo pokopališča. 
            Priporočena je ločljivost vsaj 2000x2000 pikslov za natančno označevanje grobov.
          </p>
          
          <FileUploader
            selectButtonText="Izberi fotografijo"
            labelText="ali povlecite datoteko sem"
            accept="image/*"
            uploadMode="useForm"
            onValueChanged={handleFileUpload}
            allowCanceling={true}
            allowedFileExtensions={['.jpg', '.jpeg', '.png', '.gif', '.bmp']}
            maxFileSize={10000000} // 10MB
            multiple={false}
            showFileList={true}
            disabled={isUploading}
          />
          
          {isUploading && (
            <div className="uploadingIndicator">
              <p>Nalaganje fotografije...</p>
            </div>
          )}
        </div>

        {currentCemeteryPhoto && (
          <div className="imagePreviewSection">
            <div className="imageControls">
              <h3>Poravnava fotografije in referenčne točke</h3>
              <p className="calibrationInstructions">
                <strong>Korak 1:</strong> Povlecite pin za center pokopališča in nastavite ustrezen zoom.<br />
                <strong>Korak 2:</strong> Fotografijo poravnajte na satelitski podlagi z orodji za premik, rotacijo in spreminjanje velikosti.<br />
                <strong>Korak 3:</strong> Izberite dodajanje referenčne točke in kliknite neposredno na poravnano fotografijo na zemljevidu.
              </p>
              
              <div className="controlButtons">
                <Button
                  text="Shrani kalibracijo"
                  type="default"
                  stylingMode="contained"
                  onClick={saveCemeteryCalibration}
                  disabled={isSaving || !photoFileName}
                  icon="save"
                />
                <Button
                  text="Ponastavi kalibracijo"
                  type="normal"
                  stylingMode="outlined"
                  onClick={resetCemeteryCalibration}
                  icon="refresh"
                />
                <Button
                  text={isAddingReferencePoint ? 'Prekliči dodajanje točke' : 'Dodaj referenčno točko'}
                  type={isAddingReferencePoint ? 'default' : 'normal'}
                  stylingMode={isAddingReferencePoint ? 'contained' : 'outlined'}
                  onClick={() => setIsAddingReferencePoint((prev) => !prev)}
                  icon="pin"
                />
              </div>
            </div>

            <div className="overlayEditorSection">
              <div className="cemeteryMapHeader">
                <h4>Predogled poravnave na zemljevidu</h4>
                <p>Pin določa center pokopališča. Ob aktivnem dodajanju kliknite na poravnano fotografijo za novo referenčno točko.</p>
              </div>

              <div className="cemeteryMapEditor overlayCalibrationMap">
                <MapContainer
                  key={`${selectedCemeterySlug || 'none'}-${calibrationMapInstanceKey}`}
                  center={[cemeteryLocationValue.centerLat, cemeteryLocationValue.centerLng]}
                  zoom={cemeteryLocationValue.defaultZoom}
                  scrollWheelZoom={true}
                  zoomControl={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  <ZoomControl position="bottomright" />
                  <TileLayer
                    attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    maxZoom={20}
                    minZoom={3}
                  />
                  <CemeteryLocationMapSync
                    position={[cemeteryLocationValue.centerLat, cemeteryLocationValue.centerLng]}
                    zoom={cemeteryLocationValue.defaultZoom}
                    onChange={(selection) => {
                      setCemeteryCenterLat(selection.centerLat);
                      setCemeteryCenterLng(selection.centerLng);
                      setCemeteryDefaultZoom(selection.defaultZoom);
                    }}
                  />
                  {currentCemeteryPhoto && overlayPlacement && (
                    <DistortableImageLayer
                      imageUrl={currentCemeteryPhoto}
                      placement={{ ...overlayPlacement, opacity: overlayOpacity }}
                      editable={true}
                      mode={overlayEditorMode}
                      preserveAspectRatio={preserveAspectRatio}
                      onTogglePreserveAspectRatio={() => setPreserveAspectRatio((prev) => !prev)}
                      onOverlayClick={isAddingReferencePoint ? handleReferencePointMapClick : undefined}
                      opacity={overlayOpacity}
                      onReady={(overlay) => {
                        overlayRef.current = overlay;
                      }}
                    />
                  )}
                  <CemeteryCenterPin
                    position={[cemeteryLocationValue.centerLat, cemeteryLocationValue.centerLng]}
                    onPositionChange={(position) => {
                      setCemeteryCenterLat(position[0]);
                      setCemeteryCenterLng(position[1]);
                    }}
                  />
                  <CalibrationMapReferencePointCreator
                    enabled={isAddingReferencePoint}
                    onMapClick={handleReferencePointMapClick}
                  />
                  {overlayPreviewPoints.map((point) => (
                    <CalibrationReferencePointMarker
                      key={point.id}
                      point={point}
                      dragEnabled={!isAddingReferencePoint}
                      onEdit={handleEditReferencePoint}
                      onMove={handleMoveReferencePoint}
                    />
                  ))}
                </MapContainer>
              </div>
            </div>

            <div className="calibrationInfo">
              <h4>Stanje kalibracije</h4>
              <div className="calibrationStatus">
                <p><strong>Poravnava:</strong> {overlayPlacement ? '✅ Nastavljena' : '❌ Ni nastavljena'}</p>
                <p><strong>Referenčne točke:</strong> {referencePoints.length} dodanih</p>
                <p><strong>Status:</strong> {cemeteryDataId ? '💾 Shranjeno' : '⚠️ Ni shranjeno'}</p>
              </div>
              
              {isSaving && (
                <div className="savingIndicator">
                  <p>Shranjevanje kalibracije...</p>
                </div>
              )}
              
              {referencePoints.length > 0 && (
                <div className="referencePointsList">
                  <h5>Referenčne točke:</h5>
                  <ul>
                    {referencePoints.map((point, index) => (
                      <li key={point.id}>
                        <span>{index + 1}. {point.name} [{getReferencePointIconOption(point.iconKey).glyph}] - {getReferencePointIconOption(point.iconKey).label} ({point.latitude.toFixed(6)}, {point.longitude.toFixed(6)})</span>
                        <div className="referencePointListActions">
                          <Button text="Uredi" stylingMode="text" onClick={() => handleEditReferencePoint(point.id)} />
                          <Button text="Izbriši" stylingMode="text" onClick={() => handleDeleteReferencePoint(point.id)} />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderUserManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Upravljanje uporabnikov</h2>
        <p className="sectionDescription">
          Upravljajte uporabniške račune, vloge in dovoljenja sistema
        </p>
      </div>

      <div className="gridContainer">
        <DataGrid
          ref={usersDataGridRef}
          dataSource={usersApi.createStore()}
          showBorders={true}
          showRowLines={true}
          showColumnLines={false}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
          remoteOperations={{ filtering: true, sorting: true, paging: true }}
          height="auto"
          wordWrapEnabled={true}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} placeholder="Iskanje uporabnikov..." />          
          
          <FilterPanel visible={true} />
          <Paging enabled={true} defaultPageSize={10} />
          <Pager
            visible={true}
            allowedPageSizes={allowedPageSizes}
            displayMode={displayMode}
            showPageSizeSelector={showPageSizeSelector}
            showInfo={showInfo}
            showNavigationButtons={showNavButtons} />

          <Editing 
            mode="popup" 
            allowUpdating={true} 
            allowAdding={true} 
            allowDeleting={true}
            confirmDelete={true}
            texts={{
              confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati tega uporabnika?',
              deleteRow: 'Izbriši',
              editRow: 'Uredi',
              saveRowChanges: 'Shrani',
              cancelRowChanges: 'Prekliči',
              addRow: 'Dodaj uporabnika'
            }}
          >
            <DataGridPopup 
              title="Upravljanje uporabnika" 
              showTitle={true} 
              width={600} 
              height={500}
            />
            <Form colCount={2}>
              <FormItem dataField="email" colSpan={2} />
              <FormItem dataField="firstName" />
              <FormItem dataField="lastName" />
              <FormItem dataField="isActive" />
            </Form>
          </Editing>
          
          <Toolbar>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
            <Item name="columnChooserButton" />
          </Toolbar>

          <Column 
            dataField="email" 
            caption="E-poštni naslov" 
            minWidth={180}
            validationRules={[
              { type: 'required', message: 'E-poštni naslov je obvezen' },
              { type: 'email', message: 'Napačen format e-poštnega naslova' }
            ]}
          />
          <Column 
            dataField="firstName" 
            caption="Ime" 
            minWidth={120}
            validationRules={[{ type: 'required', message: 'Ime je obvezno' }]}
          />
          <Column 
            dataField="lastName" 
            caption="Priimek" 
            minWidth={120}
            validationRules={[{ type: 'required', message: 'Priimek je obvezen' }]}
          />
          <Column 
            dataField="role" 
            caption="Vloga" 
            minWidth={140}
            allowEditing={false}
            cellRender={(_cellInfo) => (
              <span className="role-badge role-admin">Administrator</span>
            )}
          />
          <Column 
            dataField="isActive" 
            caption="Status" 
            dataType="boolean" 
            minWidth={100}
            cellRender={(cellInfo) => {
              const isActive = cellInfo.value;
              return (
                <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                  {isActive ? '✓ Aktiven' : '✗ Neaktiven'}
                </span>
              );
            }}
          />
          <Column 
            dataField="createdDate" 
            caption="Ustvarjen" 
            dataType="date" 
            minWidth={100}
            format="dd.MM.yyyy"
            allowEditing={false}
          />
          <Column 
            dataField="lastLogin" 
            caption="Zadnja prijava" 
            dataType="datetime" 
            minWidth={140}
            format="dd.MM.yyyy HH:mm"
            allowEditing={false}
            calculateDisplayValue={(rowData) => {
              if (!rowData.lastLogin) return 'Nikoli';
              const now = new Date();
              const lastLogin = new Date(rowData.lastLogin);
              const diffMs = now.getTime() - lastLogin.getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              
              if (diffDays === 0) return 'Danes';
              if (diffDays === 1) return 'Včeraj';
              if (diffDays < 7) return `Pred ${diffDays} dni`;
              return lastLogin.toLocaleDateString('sl-SI');
            }}
          />
          <Column 
            caption="Geslo" 
            minWidth={120}
            allowEditing={false}
            allowSorting={false}
            allowFiltering={false}
            cellRender={(cellInfo) => {
              const hasPassword = cellInfo.data.hasPassword; // We'll need to add this to the API response
              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {hasPassword ? (
                    <button 
                      className="password-action-btn reset"
                      onClick={() => handleResetPassword(cellInfo.data.id)}
                      title="Ponastavi geslo"
                    >
                      🔑 Ponastavi
                    </button>
                  ) : (
                    <span className="no-password-badge">Ni nastavljeno</span>
                  )}
                </div>
              );
            }}
          />
        </DataGrid>
      </div>
    </>
  );

  const renderAnalyticsManagement = () => {
    const granularityOptions: Array<{ value: VisitTrendGranularity; text: string }> = [
      { value: 'year', text: 'Letno' },
      { value: 'month', text: 'Mesečno' },
      { value: 'day', text: 'Dnevno' },
    ];

    const totalVisits = trendData.reduce((sum, item) => sum + item.totalVisits, 0);
    const totalPageViews = trendData.reduce((sum, item) => sum + item.pageViews, 0);
    const totalSelections = trendData.reduce((sum, item) => sum + item.selections, 0);
    const peakPeriod = trendData.reduce(
      (best, item) => item.totalVisits > best.visits ? { label: item.periodLabel, visits: item.totalVisits } : best,
      { label: '-', visits: 0 }
    );

    return (
      <div className="analyticsContainer">
        <div className="sectionHeader">
          <h2 className="sectionTitle">Statistika obiskov javne strani</h2>
          <p className="sectionDescription">
            Analiza obiskov strani po izbranem časovnem obdobju.
          </p>
        </div>

        <div className="analyticsFilters">
          <div className="analyticsFilterItem">
            <label>Od datuma</label>
            <DateBox
              type="date"
              value={analyticsStartDate}
              displayFormat="dd.MM.yyyy"
              onValueChanged={(e) => {
                if (e.value) {
                  setAnalyticsStartDate(e.value as Date);
                }
              }}
            />
          </div>

          <div className="analyticsFilterItem">
            <label>Do datuma</label>
            <DateBox
              type="date"
              value={analyticsEndDate}
              displayFormat="dd.MM.yyyy"
              onValueChanged={(e) => {
                if (e.value) {
                  setAnalyticsEndDate(e.value as Date);
                }
              }}
            />
          </div>

          <div className="analyticsFilterItem">
            <label>Prikaz trenda</label>
            <SelectBox
              items={granularityOptions}
              valueExpr="value"
              displayExpr="text"
              value={trendGranularity}
              onValueChanged={(e) => {
                if (e.value) {
                  setTrendGranularity(e.value as VisitTrendGranularity);
                }
              }}
            />
          </div>

          <div className="analyticsFilterItem analyticsFilterMulti">
            <label>Pokopališča</label>
            <TagBox
              dataSource={cemeteries}
              valueExpr="slug"
              displayExpr="name"
              value={analyticsSelectedCemeterySlugs}
              showSelectionControls={true}
              applyValueMode="useButtons"
              searchEnabled={true}
              multiline={false}
              onValueChanged={(e) => {
                const value = Array.isArray(e.value) ? e.value as string[] : [];
                setAnalyticsSelectedCemeterySlugs(value);
              }}
              placeholder="Vsa pokopališča"
            />
          </div>

          <div className="analyticsFilterActions">
            <Button
              text="Osveži"
              icon="refresh"
              type="default"
              stylingMode="contained"
              onClick={() => void loadVisitStatistics()}
              disabled={isAnalyticsLoading}
            />
          </div>
        </div>

        <div className="analyticsSummary">
          <div className="analyticsSummaryCard">
            <span className="analyticsSummaryLabel">Skupaj dogodkov</span>
            <span className="analyticsSummaryValue">{totalVisits}</span>
          </div>
          <div className="analyticsSummaryCard">
            <span className="analyticsSummaryLabel">Ogledi strani</span>
            <span className="analyticsSummaryValue">{totalPageViews}</span>
          </div>
          <div className="analyticsSummaryCard">
            <span className="analyticsSummaryLabel">Iskanja pokojnikov</span>
            <span className="analyticsSummaryValue">{totalSelections}</span>
          </div>
          <div className="analyticsSummaryCard">
            <span className="analyticsSummaryLabel">Najbolj prometno obdobje</span>
            <span className="analyticsSummaryValue" style={{ fontSize: '18px' }}>{peakPeriod.label}</span>
            <span className="analyticsSummaryLabel">{peakPeriod.visits > 0 ? `${peakPeriod.visits} obiskov` : '—'}</span>
          </div>
        </div>

        <div className="analyticsChartBlock">
          <h3>Trend dogodkov (letno, mesečno, dnevno)</h3>
          <Chart dataSource={trendData} palette="Soft Blue">
            <CommonSeriesSettings argumentField="periodLabel" type="line" />
            <Series valueField="totalVisits" name="Skupaj" />
            <Series valueField="pageViews" name="Ogledi strani" />
            <Series valueField="selections" name="Izbire pokojnika" />
            <ArgumentAxis argumentType="string" />
            <Legend visible={true} verticalAlignment="bottom" horizontalAlignment="center" />
            <Tooltip enabled={true} />
          </Chart>
        </div>

        <div className="analyticsChartGrid">
          <div className="analyticsChartBlock">
            <h3>Obiski po dnevih v tednu</h3>
            <Chart dataSource={weekdayData} palette="Material">
              <CommonSeriesSettings argumentField="label" type="bar" />
              <Series valueField="pageViews" name="Ogledi strani" />
              <Series valueField="selections" name="Iskanja pokojnikov" />
              <ArgumentAxis argumentType="string" />
              <Legend visible={true} verticalAlignment="bottom" horizontalAlignment="center" />
              <Tooltip enabled={true} />
            </Chart>
          </div>

          <div className="analyticsChartBlock">
            <h3>Obiski po urah dneva</h3>
            <Chart dataSource={hourlyData} palette="Harmony Light">
              <CommonSeriesSettings argumentField="label" type="bar" />
              <Series valueField="pageViews" name="Ogledi strani" />
              <Series valueField="selections" name="Iskanja pokojnikov" />
              <ArgumentAxis argumentType="string" />
              <Legend visible={true} verticalAlignment="bottom" horizontalAlignment="center" />
              <Tooltip enabled={true} />
            </Chart>
          </div>
        </div>

        {topDeceasedData.length > 0 && (
          <div className="analyticsChartBlock">
            <h3>Top 10 iskanih pokojnikov v izbranem obdobju</h3>
            <Chart
              dataSource={[...topDeceasedData]
                .reverse()
                .map((d) => ({
                  ...d,
                  displayName: `${d.fullName} (${d.birthYear ?? '?'}-${d.deathYear ?? '?'})`,
                }))}
              rotated={true}
              palette="Soft Blue"
            >
              <CommonSeriesSettings argumentField="displayName" type="bar" />
              <Series valueField="selectionCount" name="Iskanja" />
              <ArgumentAxis argumentType="string" />
              <Legend visible={false} />
              <Tooltip enabled={true} />
            </Chart>
          </div>
        )}

        <LoadPanel
          visible={isAnalyticsLoading}
          showIndicator={true}
          showPane={true}
          shading={true}
          hideOnOutsideClick={false}
          message="Nalagam statistiko..."
        />
      </div>
    );
  };

  return (
    <div className="viewport">
      <div className="container">
        {/* Header */}
        <div className="header">
          <div className="headerContent">
            <div className="brandSection">
              <h1 className="brandTitle">GraveSearch</h1>
              <p className="brandSubtitle">Admin nadzorna plošča</p>
            </div>
            
            {/* Navigation */}
            <div className="navigationSection">
              <Tabs
                dataSource={navigationTabs}
                selectedIndex={selectedTabIndex}
                onSelectionChanged={(e) => {
                  if (e.addedItems && e.addedItems.length > 0) {
                    setActiveTab(e.addedItems[0].id as ActiveTab);
                  }
                }}
              />
            </div>
            
            <div className="userSection">
              <div className="userInfo">
                <span className="userName">
                  {user?.firstName} {user?.lastName}
                </span>
                <div className="cemeterySelectorControls cemeterySelectorCompact">
                  <Button
                    icon="plus"
                    hint="Dodaj pokopališče"
                    stylingMode="outlined"
                    type="normal"
                    height={32}
                    width={32}
                    onClick={() => setIsCreateCemeteryPopupVisible(true)}
                  />
                  <SelectBox
                    items={cemeteries}
                    value={selectedCemeterySlug || null}
                    valueExpr="slug"
                    displayExpr="name"
                    width={210}
                    height={32}
                    placeholder="Izberite pokopališče"
                    onValueChanged={(e) => {
                      const slug = e.value as string;
                      setSelectedCemeterySlug(slug || '');
                      if (slug) {
                        localStorage.setItem('adminSelectedCemeterySlug', slug);
                      }
                    }}
                  />
                </div>
              </div>
              <Button
                text="Odjava"
                type="normal"
                stylingMode="contained"
                onClick={handleLogout}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="contentSection">
          {activeTab === 'deceased' && renderDeceasedManagement()}
          {activeTab === 'users' && renderUserManagement()}
          {activeTab === 'cemetery' && renderCemeteryManagement()}
          {activeTab === 'analytics' && renderAnalyticsManagement()}
        </div>
      </div>

      <Popup
        visible={isCreateCemeteryPopupVisible}
        onHiding={() => setIsCreateCemeteryPopupVisible(false)}
        title="Dodaj pokopališče"
        width={460}
        height={360}
        showTitle={true}
        dragEnabled={true}
      >
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Ime pokopališča</label>
            <TextBox
              value={newCemeteryName}
              onValueChanged={(e: any) => {
                const value = e.value ?? '';
                setNewCemeteryName(value);
                if (!newCemeterySlug) {
                  setNewCemeterySlug(slugify(value));
                }
              }}
              placeholder="Npr. Pokopališče Zagorje ob Savi"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Slug (URL)</label>
            <TextBox
              value={newCemeterySlug}
              onValueChanged={(e: any) => setNewCemeterySlug(slugify(e.value ?? ''))}
              placeholder="pokopalisce-zagorje-ob-savi"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
            <Button text="Prekliči" stylingMode="outlined" onClick={() => setIsCreateCemeteryPopupVisible(false)} />
            <Button
              text="Shrani"
              type="default"
              stylingMode="contained"
              onClick={handleCreateCemetery}
              disabled={isCreatingCemetery}
            />
          </div>
        </div>
      </Popup>

      <Popup
        visible={isReferencePointPopupVisible}
        onHiding={() => {
          setIsReferencePointPopupVisible(false);
          setReferencePointDraft(null);
        }}
        title="Referenčna točka"
        width={420}
        height={500}
        showTitle={true}
        dragEnabled={true}
      >
        <div className="referencePointPopupContent">
          <div className="referencePointPopupField">
            <label>Ime</label>
            <TextBox
              value={referencePointDraft?.name ?? ''}
              onValueChanged={(e: any) => setReferencePointDraft((prev) => (prev ? { ...prev, name: e.value ?? '' } : prev))}
            />
          </div>
          <div className="referencePointPopupField">
            <label>Ikona</label>
            <div className="referencePointSelectWithPreview">
              <span
                className="referencePointIconPreview"
                style={{ backgroundColor: getReferencePointIconOption(referencePointDraft?.iconKey ?? 'gate').color }}
                aria-hidden="true"
              >
                {getReferencePointIconOption(referencePointDraft?.iconKey ?? 'gate').glyph}
              </span>
              <SelectBox
                items={REFERENCE_POINT_ICON_OPTIONS}
                valueExpr="key"
                displayExpr="label"
                value={referencePointDraft?.iconKey ?? 'gate'}
                itemRender={(item) => renderReferencePointIconOption(item as ReferencePointIconOption | null)}
                onValueChanged={(e) => setReferencePointDraft((prev) => (prev ? { ...prev, iconKey: (e.value as string) || 'other' } : prev))}
              />
            </div>
          </div>
          <div className="referencePointPopupField">
            <label>Opis</label>
            <TextBox
              value={referencePointDraft?.description ?? ''}
              onValueChanged={(e: any) => setReferencePointDraft((prev) => (prev ? { ...prev, description: e.value ?? '' } : prev))}
            />
          </div>
          <div className="referencePointPopupField">
            <label>Javno prikazi</label>
            <CheckBox
              text="Prikaži točko na javnem zemljevidu"
              value={referencePointDraft?.showOnPublicMap ?? true}
              onValueChanged={(e) => setReferencePointDraft((prev) => (prev ? { ...prev, showOnPublicMap: !!e.value } : prev))}
            />
          </div>
          <div className="referencePointPopupActions">
            <Button text="Prekliči" stylingMode="outlined" onClick={() => {
              setIsReferencePointPopupVisible(false);
              setReferencePointDraft(null);
            }} />
            <Button text="Shrani točko" type="default" stylingMode="contained" onClick={handleSaveReferencePoint} />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default Administration;